import { formatScaled, parseScaled } from './decimal.ts'

export const RATIO_SCALE = 10

/** Ten integer digits, ten fraction digits - same precision class as {@link RATE_SCALE}. */
export const RATIO_PATTERN = /^-?\d{1,10}(\.\d{1,10})?$/

declare const RatioBrand: unique symbol

/**
 * A dimensionless decimal - a percentage, not a currency amount and not an exchange rate.
 * Unlike {@link Rate} it carries no currency pair: `expense_income_ratio` and
 * `liability_asset_ratio` compare two amounts in whatever currency they were computed in
 * and the result has none of its own.
 */
export interface Ratio {
  readonly units: bigint
  readonly [RatioBrand]: true
}

export function ratio(value: string): Ratio {
  return { units: parseScaled(value, RATIO_SCALE, RATIO_PATTERN) } as Ratio
}

/** Parses a percent-typed input (`"25"` for 25%) into the stored fraction (`0.25`). Exact: `10n ** RATIO_SCALE` is always divisible by 100. */
export function ratioFromPercent(value: string): Ratio {
  return { units: parseScaled(value, RATIO_SCALE, RATIO_PATTERN) / 100n } as Ratio
}

export function toApiRatio(value: Ratio): string {
  return formatScaled(value.units, RATIO_SCALE)
}

export function compareRatio(a: Ratio, b: Ratio): -1 | 0 | 1 {
  return a.units < b.units ? -1 : a.units > b.units ? 1 : 0
}

export function subtractRatio(a: Ratio, b: Ratio): Ratio {
  return { units: a.units - b.units } as Ratio
}

/**
 * A ratio as a plain 0..1 fraction, for chart geometry only - never for a displayed figure.
 * Shares the one unavoidable float division with {@link moneyProportion}.
 */
export function ratioFraction(value: Ratio): number {
  return Number(value.units) / 10 ** RATIO_SCALE
}

export function absRatio(value: Ratio): Ratio {
  return value.units < 0n ? ({ units: -value.units } as Ratio) : value
}

/** The fraction as a plain number clamped to 0..1, for a progress bar's geometry only - never a displayed figure. */
export function ratioProportion(value: Ratio): number {
  const fraction = Number(value.units) / Number(10n ** BigInt(RATIO_SCALE))
  return Math.min(1, Math.max(0, fraction))
}
